(() => {
  'use strict';

  const config = window.DekorkProductCardSettings || {};
  if (config.enabled === false) return;

  const CARD_SELECTOR = [
    '.dk-global-product-visual',
    '.dk-mini-cart__item-media',
    '.dk-cart-line__image',
    '.dk-cart__item-media',
    '.cart-item__media',
    '.dk-predictive__media',
    '.dkp2__variant-card--image i'
  ].join(',');
  const IMAGE_SELECTOR = 'img';
  const processed = new WeakSet();
  const hoverBound = new WeakSet();

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  function rgbToHsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;
    const d = max - min;

    if (d !== 0) {
      s = l > .5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)); break;
        case g: h = ((b - r) / d + 2); break;
        default: h = ((r - g) / d + 4);
      }
      h /= 6;
    }

    return [h * 360, s, l];
  }

  function hslToRgb(h, s, l) {
    h = ((h % 360) + 360) % 360 / 360;
    let r;
    let g;
    let b;

    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };
      const q = l < .5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
  }

  function detectAccent(image) {
    try {
      if (!image.naturalWidth || !image.naturalHeight) return null;

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return null;

      canvas.width = 64;
      canvas.height = 64;
      context.clearRect(0, 0, 64, 64);
      context.drawImage(image, 0, 0, 64, 64);

      const pixels = context.getImageData(0, 0, 64, 64).data;
      const buckets = new Map();

      for (let i = 0; i < pixels.length; i += 4) {
        const alpha = pixels[i + 3];
        if (alpha < 80) continue;

        const red = pixels[i];
        const green = pixels[i + 1];
        const blue = pixels[i + 2];
        const [hue, saturation, lightness] = rgbToHsl(red, green, blue);

        if (lightness < .08 || lightness > .92 || saturation < .20) continue;

        const bucketKey = Math.round(hue / 12) * 12;
        const weight = Math.pow(saturation, 1.7) * (1 - Math.abs(lightness - .52)) * 2;
        const bucket = buckets.get(bucketKey) || {
          weight: 0,
          hue: 0,
          saturation: 0,
          lightness: 0
        };

        bucket.weight += weight;
        bucket.hue += hue * weight;
        bucket.saturation += saturation * weight;
        bucket.lightness += lightness * weight;
        buckets.set(bucketKey, bucket);
      }

      let best = null;
      buckets.forEach(bucket => {
        if (!best || bucket.weight > best.weight) best = bucket;
      });

      if (!best || best.weight < 1) return null;

      const hue = best.hue / best.weight;
      const rawSaturation = best.saturation / best.weight;
      const rawLightness = best.lightness / best.weight;

      // Balance the detected color by hue instead of forcing every lamp to
      // the same high saturation. Green/cyan pixels are naturally dominant
      // in many product renders, so they need gentler output. Blue and purple
      // receive an optional, visible boost controlled by Theme Settings.
      const isGreen = hue >= 72 && hue < 178;
      const isBluePurple = hue >= 190 && hue <= 315;
      const bluePurpleBoost = config.bluePurpleBoost !== false;

      let saturation;
      let lightness;

      if (isGreen) {
        saturation = clamp(rawSaturation * .82, .42, .78);
        lightness = clamp(rawLightness * .94, .43, .57);
      } else if (isBluePurple && bluePurpleBoost) {
        saturation = clamp(rawSaturation * 1.34, .76, 1);
        lightness = clamp(rawLightness * 1.10 + .035, .54, .70);
      } else {
        saturation = clamp(rawSaturation * 1.08, .56, .92);
        lightness = clamp(rawLightness * 1.01, .48, .64);
      }

      const [r, g, b] = hslToRgb(hue, saturation, lightness);
      return `rgb(${r} ${g} ${b})`;
    } catch (error) {
      return null;
    }
  }

  function detectAccentFromUrl(image) {
    return new Promise(resolve => {
      const source = image.currentSrc || image.src;
      if (!source) {
        resolve(null);
        return;
      }

      const sample = new Image();
      sample.crossOrigin = 'anonymous';
      sample.decoding = 'async';
      sample.onload = () => resolve(detectAccent(sample));
      sample.onerror = () => resolve(null);
      sample.src = source;
    });
  }

  function applyGlowColor(visual, color, source) {
    if (!visual) return;

    const card = visual.closest('.dk-card, .dk-mini-cart__item, .dk-cart-line, .dk-cart__item, .cart-item, .dk-predictive__item, .dkp2__variant-card');

    // Every compact product visual reads its glow from the media wrapper.
    visual.style.setProperty('--dk-product-glow', color);
    visual.style.setProperty('--dk-lamp-glow', color);
    visual.dataset.dkGlowSource = source;

    // Keep the nearest item root synchronized for hover layers and any
    // text/price styling that inherits the product glow variable.
    if (card) {
      card.style.setProperty('--dk-product-glow', color);
      card.style.setProperty('--dk-lamp-glow', color);
      card.dataset.dkGlowSource = source;
    }
  }

  async function updateVisual(visual, force = false) {
    if (!visual || (!force && processed.has(visual))) return;

    const image = visual.querySelector(IMAGE_SELECTOR);
    const fallback = config.fallbackGlow || '#49e8d0';

    if (!config.followImage || !image) {
      applyGlowColor(visual, fallback, 'fallback');
      processed.add(visual);
      return;
    }

    try {
      if (!image.complete || !image.naturalWidth) {
        await new Promise(resolve => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        });
      }

      if (typeof image.decode === 'function') {
        try { await image.decode(); } catch (_) {}
      }

      // The rendered Shopify CDN image can be canvas-tainted on custom domains.
      // Try it first, then retry through an anonymous CORS image using the same URL.
      let detected = detectAccent(image);
      if (!detected) detected = await detectAccentFromUrl(image);
      applyGlowColor(visual, detected || fallback, detected ? 'image' : 'fallback');
    } catch (error) {
      applyGlowColor(visual, fallback, 'fallback');
    }

    processed.add(visual);
  }

  function bindHoverRefresh(visual) {
    if (!config.recalculateOnHover || hoverBound.has(visual)) return;
    hoverBound.add(visual);
    visual.addEventListener('mouseenter', () => updateVisual(visual, true), { passive: true });
    visual.addEventListener('focusin', () => updateVisual(visual, true), { passive: true });
  }

  const observed = new WeakSet();
  const scheduleIdle = window.requestIdleCallback
    ? callback => window.requestIdleCallback(callback, { timeout: 1200 })
    : callback => window.setTimeout(callback, 80);

  const visibilityObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          visibilityObserver.unobserve(entry.target);
          scheduleIdle(() => updateVisual(entry.target));
        });
      }, { rootMargin: '240px 0px' })
    : null;

  function queueVisual(visual) {
    if (!visual || observed.has(visual)) return;
    observed.add(visual);
    bindHoverRefresh(visual);
    if (visibilityObserver) visibilityObserver.observe(visual);
    else scheduleIdle(() => updateVisual(visual));
  }

  function scan(scope = document) {
    if (scope.matches?.(CARD_SELECTOR)) queueVisual(scope);
    scope.querySelectorAll?.(CARD_SELECTOR).forEach(queueVisual);
  }

  function init() {
    scan(document);

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) scan(node);
        });
      });
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', event => scan(event.target));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
